import React from "react";
import { Drawer, EmptyState } from "../../shared";
import { useGetDealerRolesFirmsQuery } from "../../../store/dealerRoles/firmApi";
import { useGetDealerRolesDeviceDetailQuery } from "../../../store/dealerRoles/devicesApi";
import useAuth from "../../../Hooks/useAuth";

/**
 * Üst durum kutusuna tıklanınca açılan liste çekmecesi.
 *
 * Kutunun sayısını ÜRETEN kaydı gösterir: firma kutularında firma listesi,
 * cihaz kutularında o konumdaki cihazlar. Veri kutudaki sayıyla aynı kaynaktan
 * gelmez (özet ayrı bir uçtan geliyor), bu yüzden liste kendi ucunu çağırır ve
 * yalnızca çekmece AÇIKKEN sorgu atar (skip).
 *
 * Yetki kontrolü çağıran tarafta (OverviewStats): yetkisi olmayan kutu zaten
 * tıklanabilir değil.
 */

const STATUS_BADGE = {
  Depo: "bg-amber-50 text-amber-700 border-amber-100",
  Firma: "bg-blue-50 text-blue-700 border-blue-100",
  Servis: "bg-purple-50 text-purple-700 border-purple-100",
  Plasiyer: "bg-red-50 text-red-700 border-red-100",
};

function Row({ title, subtitle, badge, badgeClass }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-gray-200 bg-white">
      <div className="min-w-0">
        <p className="text-sm font-SemiBold text-gray-800 truncate">{title}</p>
        {subtitle && (
          <p className="text-xs text-gray-500 truncate mt-0.5">{subtitle}</p>
        )}
      </div>
      {badge && (
        <span
          className={`shrink-0 text-[11px] font-SemiBold px-2 py-0.5 rounded-full border ${badgeClass || "bg-gray-50 text-gray-600 border-gray-100"}`}
        >
          {badge}
        </span>
      )}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-xl border border-gray-200 bg-gray-50 animate-pulse"
        />
      ))}
    </div>
  );
}

export default function OverviewDrawer({ open, card, onClose }) {
  const { auth } = useAuth();
  const token = auth?.accessToken;

  const isFirmCard = card?.kind === "firms";
  const isDeviceCard = card?.kind === "devices";

  const firmsQuery = useGetDealerRolesFirmsQuery(
    { token },
    { skip: !open || !isFirmCard || !token },
  );
  const devicesQuery = useGetDealerRolesDeviceDetailQuery(
    { token, dealerDeviceStatusName: card?.status, page: 1, limit: 100 },
    { skip: !open || !isDeviceCard || !token },
  );

  const loading = isFirmCard ? firmsQuery.isLoading : devicesQuery.isLoading;
  const isError = isFirmCard ? firmsQuery.isError : devicesQuery.isError;

  const firms = (firmsQuery.data?.data?.firms || []).filter(
    (f) => f.dealerRelation === card?.relation,
  );
  const devices = devicesQuery.data?.devices || [];

  const rows = isFirmCard ? firms : devices;

  return (
    <Drawer open={open} onClose={onClose} title={card?.title || ""} width="lg">
      <Drawer.Body className="p-4">
        {loading ? (
          <SkeletonRows />
        ) : isError ? (
          <EmptyState
            message="Liste yüklenemedi"
            suggestion="Bağlantıyı kontrol edip tekrar deneyin."
          />
        ) : rows.length === 0 ? (
          <EmptyState message="Kayıt yok" />
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-gray-400 mb-1">{rows.length} kayıt</p>
            {isFirmCard
              ? firms.map((f) => (
                  <Row
                    key={f._id}
                    title={f.firmName}
                    subtitle={f.bayserNo ? `Bayser No: ${f.bayserNo}` : null}
                    badge={f.isActive ? "Aktif" : "Pasif"}
                    badgeClass={
                      f.isActive
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                        : "bg-red-50 text-red-700 border-red-100"
                    }
                  />
                ))
              : devices.map((d) => (
                  <Row
                    key={d._id}
                    title={d.customName || d.deviceName || d.serialNo}
                    subtitle={[d.serialNo, d.firm?.firmName]
                      .filter(Boolean)
                      .join(" · ")}
                    badge={d.dealerDeviceStatusName || "-"}
                    badgeClass={STATUS_BADGE[d.dealerDeviceStatusName]}
                  />
                ))}
          </div>
        )}
      </Drawer.Body>
    </Drawer>
  );
}
